import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { LuQuote } from 'react-icons/lu';
import { TbBrandLinkedin } from 'react-icons/tb';

import { CLIENT_TESTIMONIALS } from './testimonials';

const TESTIMONIAL_INTERVAL_MS = 6000;
const QUOTE_GLYPH_COLOR = 'var(--color-panel-b)';

const stylesheet = {
  card: 'max-w-[430px] rounded-semantic p-[30px_32px] border',
  quoteChip:
    'h-[46px] w-[46px] rounded-semantic bg-[rgba(255,255,255,0.92)] grid place-items-center mb-5',
  quoteText: 'text-white leading-[1.42] tracking-[-0.01em]',
  authorRow: 'flex items-center gap-[13px] mt-6',
  avatar:
    'h-[46px] w-[46px] rounded-full object-cover border-2 border-[rgba(255,255,255,0.5)] shrink-0',
  authorMeta: 'min-w-0',
  nameRow: 'flex items-center gap-2',
  authorName: 'text-white font-bold text-[15px]',
  authorRole: 'text-[13.5px]',
  linkedin: 'shrink-0 text-white/70 hover:text-white transition-colors',
};

const CARD_GLASS_STYLE = {
  background:
    'linear-gradient(135deg, rgba(255,255,255,0.22), rgba(255,255,255,0.10))',
  border: '1px solid rgba(255,255,255,0.28)',
  backdropFilter: 'blur(16px) saturate(1.1)',
  WebkitBackdropFilter: 'blur(16px) saturate(1.1)',
  boxShadow: '0 24px 60px -24px rgba(0,0,0,0.45)',
} as const;

const AUTHOR_ROLE_STYLE = { color: 'rgba(255,255,255,0.72)' } as const;

const QUOTE_FONT_SIZE_BY_LENGTH = [
  { max: 160, size: '22px' },
  { max: 300, size: '20px' },
  { max: 440, size: '18px' },
  { max: 560, size: '16px' },
  { max: Number.POSITIVE_INFINITY, size: '15px' },
];

function getQuoteTextStyle(quote: string) {
  const tier = QUOTE_FONT_SIZE_BY_LENGTH.find((t) => quote.length <= t.max);
  return { fontSize: tier?.size ?? '15px', fontWeight: 500 } as const;
}

export function AuthTestimonialCard(): ReactNode {
  const { t } = useTranslation();
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setIndex((i) => (i + 1) % CLIENT_TESTIMONIALS.length);
    }, TESTIMONIAL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, []);

  const client = CLIENT_TESTIMONIALS[index];
  const name = t(`auth.testimonials.${client.id}.name`);
  const role = t(`auth.testimonials.${client.id}.role`);
  const quote = t(`auth.testimonials.${client.id}.quote`);

  return (
    <div className={stylesheet.card} style={CARD_GLASS_STYLE}>
      <div className={stylesheet.quoteChip}>
        <LuQuote color={QUOTE_GLYPH_COLOR} />
      </div>
      <p className={stylesheet.quoteText} style={getQuoteTextStyle(quote)}>
        {quote}
      </p>
      <div className={stylesheet.authorRow}>
        <img className={stylesheet.avatar} src={client.avatar} alt={name} />
        <div className={stylesheet.authorMeta}>
          <div className={stylesheet.nameRow}>
            <p className={stylesheet.authorName}>{name}</p>
            <a
              className={stylesheet.linkedin}
              href={client.linkedinUrl}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={t('auth.viewLinkedin')}
            >
              <TbBrandLinkedin />
            </a>
          </div>
          <p className={stylesheet.authorRole} style={AUTHOR_ROLE_STYLE}>
            {role}
          </p>
        </div>
      </div>
    </div>
  );
}
